/**
 * The text to show for a failed API call.
 *
 * FastAPI answers a handler's own `HTTPException` with `detail` as a string, but a request-body
 * validation failure (a Pydantic 422) with `detail` as an array of `{ loc, msg, type }` objects, and
 * some handlers raise `detail` as an object (`{ code, message, ... }`). Handing either to
 * `toast.error` makes React try to render plain objects as children, and the `<Toaster>` sits at the
 * App root, outside the layout's error boundary, so the throw blanks the page. This turns every shape
 * into a string: a string detail as it is, an array as its entries' `msg` joined, an object as its
 * non-empty string `message`.
 *
 * Pydantic prefixes a validator's own message with "Value error, "; that is dropped, because it
 * describes the library rather than the problem.
 *
 * Always returns a string, even when `fallback` is not one, so the result is safe to hand to a toast.
 *
 * @param {object} error the rejection an `api` call throws (axios error).
 * @param {string} fallback shown when the response carries no usable detail.
 * @returns {string}
 */
export function errorDetailMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (typeof item === "string" ? item : item?.msg))
      .filter((msg) => typeof msg === "string" && msg)
      .map((msg) => msg.replace(/^Value error, /, ""));
    if (messages.length) return messages.join("; ");
  } else if (detail && typeof detail === "object" && typeof detail.message === "string" && detail.message) {
    return detail.message.replace(/^Value error, /, "");
  }
  return typeof fallback === "string" ? fallback : "";
}
